/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";
// import Fade from "@mui/material/Fade";

// Material Dashboard 2 React base styles
import colors from "common/assets/theme-dark/base/colors";
import breakpoints from "common/assets/theme-dark/base/breakpoints";
import typography from "common/assets/theme-dark/base/typography";
import boxShadows from "common/assets/theme-dark/base/boxShadows";
import borders from "common/assets/theme-dark/base/borders";
import globals from "common/assets/theme-dark/base/globals";

// Material Dashboard 2 React helper functions
import boxShadow from "common/assets/theme-dark/functions/boxShadow";
import hexToRgb from "common/assets/theme-dark/functions/hexToRgb";
import linearGradient from "common/assets/theme-dark/functions/linearGradient";
import pxToRem from "common/assets/theme-dark/functions/pxToRem";
import rgba from "common/assets/theme-dark/functions/rgba";

// Material Dashboard 2 React components base styles for @mui material components
import sidenav from "common/assets/theme-dark/components/sidenav";
import list from "common/assets/theme-dark/components/list";
import listItem from "common/assets/theme-dark/components/list/listItem";
import listItemText from "common/assets/theme-dark/components/list/listItemText";
import card from "common/assets/theme-dark/components/card";
import cardMedia from "common/assets/theme-dark/components/card/cardMedia";
import cardContent from "common/assets/theme-dark/components/card/cardContent";
import button from "common/assets/theme-dark/components/button";
import iconButton from "common/assets/theme-dark/components/iconButton";
import input from "common/assets/theme-dark/components/form/input";
import inputLabel from "common/assets/theme-dark/components/form/inputLabel";
import inputOutlined from "common/assets/theme-dark/components/form/inputOutlined";
import textField from "common/assets/theme-dark/components/form/textField";
import menu from "common/assets/theme-dark/components/menu";
import menuItem from "common/assets/theme-dark/components/menu/menuItem";
import switchButton from "common/assets/theme-dark/components/form/switchButton";
import divider from "common/assets/theme-dark/components/divider";
import tableContainer from "common/assets/theme-dark/components/table/tableContainer";
import tableHead from "common/assets/theme-dark/components/table/tableHead";
import tableCell from "common/assets/theme-dark/components/table/tableCell";
import linearProgress from "common/assets/theme-dark/components/linearProgress";
import breadcrumbs from "common/assets/theme-dark/components/breadcrumbs";
import slider from "common/assets/theme-dark/components/slider";
import avatar from "common/assets/theme-dark/components/avatar";
import tooltip from "common/assets/theme-dark/components/tooltip";
import appBar from "common/assets/theme-dark/components/appBar";
import tabs from "common/assets/theme-dark/components/tabs";
import tab from "common/assets/theme-dark/components/tabs/tab";
import stepper from "common/assets/theme-dark/components/stepper";
import step from "common/assets/theme-dark/components/stepper/step";
import stepConnector from "common/assets/theme-dark/components/stepper/stepConnector";
import stepLabel from "common/assets/theme-dark/components/stepper/stepLabel";
import stepIcon from "common/assets/theme-dark/components/stepper/stepIcon";
import select from "common/assets/theme-dark/components/form/select";
import formControlLabel from "common/assets/theme-dark/components/form/formControlLabel";
import formLabel from "common/assets/theme-dark/components/form/formLabel";
import checkbox from "common/assets/theme-dark/components/form/checkbox";
import radio from "common/assets/theme-dark/components/form/radio";
import autocomplete from "common/assets/theme-dark/components/form/autocomplete";
import container from "common/assets/theme-dark/components/container";
import popover from "common/assets/theme-dark/components/popover";
import buttonBase from "common/assets/theme-dark/components/buttonBase";
import icon from "common/assets/theme-dark/components/icon";
import svgIcon from "common/assets/theme-dark/components/svgIcon";
import link from "common/assets/theme-dark/components/link";
import dialog from "common/assets/theme-dark/components/dialog";
import dialogTitle from "common/assets/theme-dark/components/dialog/dialogTitle";
import dialogContent from "common/assets/theme-dark/components/dialog/dialogContent";
import dialogContentText from "common/assets/theme-dark/components/dialog/dialogContentText";
import dialogActions from "common/assets/theme-dark/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
